/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../ProposalDAOImpl';
import { ProposalFieldDAO } from '../ProposalFieldDAO';
import { ComparisonItemDAO } from '../ProposalItemDAO';
import { DataPoints } from '../DataPoints';
import { PORTFOLIO_ASSET_ALLOCATION } from '@/enums/AssetAllocation';
import { TextCell, PercentageCell, CurrencyCell, UpDownCell } from '@/views/components/cells/index';
import * as ProposalNS from '@/modules/proposal/Proposal';
import * as ProposalElementNS from '@/modules/proposal/ProposalElements';
import { ProposalStyleClassNames } from '@/modules/proposal/ProposalStyleClassNames';

// Utils
import { formatLocalizedString, formatPercentage, LOCALE } from '@/utils/i18n';
import { formatRiskProfileTargetMinMax, shouldSplitCashAndFixedIncome } from '@/utils/risk-profiles';
import { CURRENCY } from '@/utils/currency';
import { ASSET_ALLOCATION_GROUPS, COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { deepClone } from '@/utils/objects';
import { userHasPermissionEnabled, PERMISSIONS } from '@/utils/permissions';
import { PROPOSAL_DISCLAIMERS, PROPOSAL_SECTION_KEYS } from '@/modules/proposal/utils/constants';
import SeriesData from '@/types/series-data';
import DonutData from '@/types/donut-data';
import AppRegion from '@/types/app-region';
import Dictionary from '@/types/dictionary';
import LocalizedString from '@/types/localized-string';
import { DATA_PROVIDER, getDataProviderForDisplay } from '@/utils/users';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import {
  sortSubAllocations,
  sortSubAllocationsForDonut,
  calculateAssetAllocationMaxRowsPerPage,
  calculateAssetAllocationDonutMaxItemsPerPage,
} from '@/utils/portfolio/allocation';
import ProposalTableData from '@/types/proposal-table-data';

/**
 * @class AssetAllocation Data Access Object
 */
/**
 * Data Access Object (DAO) for managing asset allocation data in proposals.
 * This class extends ComparisonItemBasedProposalDAO and provides functionality
 * to build, display, and manipulate asset allocation tables and charts, including
 * support for sub-allocations, grouping, totals, and pagination.
 *
 * It handles configuration options such as grouping by asset types (e.g., Cash, Fixed Income, Equity),
 * displaying target allocations, capital values, and adjustments based on risk profiles.
 * The DAO supports both table and donut chart representations, with features like
 * row header colors, extra columns, and overflow paging for large datasets.
 *
 * @example
 * ```typescript
 * const dao = AssetAllocationDAO.buildInstance(proposal, proposalElement);
 * dao.build();
 * const tableData = dao.getTableData();
 * ```
 *
 * @public
 */
export default class AssetAllocationDAO extends ComparisonItemBasedProposalDAO {
  groupByObj: ProposalNS.GroupByAllocationsDAOOptions = {
    totalPosition: ProposalNS.DAOGroupByTotalPosition.BOTTOM,
    showGroups: false,
    showElements: true,
    showTotals: false,
    showGrandTotal: false,
    order: [
      {
        groupKey: ASSET_ALLOCATION_GROUPS.CASH,
        keys: [PORTFOLIO_ASSET_ALLOCATION.CASH],
      },
      {
        groupKey: ASSET_ALLOCATION_GROUPS.FIXED_INCOME,
        keys: [PORTFOLIO_ASSET_ALLOCATION.FIXED_INCOME],
        labelKey: 'FIXED_INCOME_EX_CASH',
      },
      {
        groupKey: ASSET_ALLOCATION_GROUPS.EQUITY,
        keys: [PORTFOLIO_ASSET_ALLOCATION.EQ, PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ, PORTFOLIO_ASSET_ALLOCATION.US_EQ, PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ],
      },
      ...(userHasPermissionEnabled(this.proposal.appUser, PERMISSIONS.PREQIN_ALT)
        ? [
            {
              groupKey: ASSET_ALLOCATION_GROUPS.ALTERNATIVE_INVESTMENT,
              keys: [PORTFOLIO_ASSET_ALLOCATION.ALTERNATIVE_INVESTMENT],
            },
          ]
        : []),
      {
        groupKey: ASSET_ALLOCATION_GROUPS.OTHER,
        keys: [PORTFOLIO_ASSET_ALLOCATION.OTHER],
      },
    ],
  };

  showRowHeaderColors: boolean = true;

  defaultGroupByObj: ProposalNS.GroupByAllocationsDAOOptions = this.groupByObj;

  extrasColumnObj: ProposalNS.ExtraColumnsDAOOptions | null = null;

  displayCapital: boolean = false;

  displayTargetAllocations: boolean = false;

  displayTargetAllocationAdjustments: boolean = false;

  itemMissingAllocationPortion: number = 0;

  hasOtherCategoryWithValue: boolean = false; // Track if any item has Other > 0 for asterisk display

  isRiskProfileTable: boolean = false;

  labelOverride?: Dictionary<LocalizedString>;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AssetAllocationDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ASSET_ALLOCATION_COLORS', 'RISK_ALLOCATION_COLORS', 'RISK_LEVELS', 'TIER_3_ASSET_ALLOCATION_COLORS'];
  }

  protected build(): void {
    // test for options
    const daoConfig: ProposalNS.AssetAllocationConfig = this.proposalElement.DAO as ProposalNS.AssetAllocationConfig;
    if (daoConfig.groupByAllocationsDAOOptions !== undefined) {
      const options: ProposalNS.GroupByAllocationsDAOOptions = daoConfig.groupByAllocationsDAOOptions;
      if (options.totalPosition !== undefined) {
        this.groupByObj.totalPosition = options.totalPosition;
      }

      if (options.showGroups !== undefined) {
        this.groupByObj.showGroups = options.showGroups;
      }

      if (options.showElements !== undefined) {
        this.groupByObj.showElements = options.showElements;
      }

      if (options.showTotals !== undefined) {
        this.groupByObj.showTotals = options.showTotals;
      }

      if (options.showGrandTotal !== undefined) {
        this.groupByObj.showGrandTotal = options.showGrandTotal;
      }

      if (options.order !== undefined) {
        this.groupByObj.order = options.order;
      }

      // Create backup for group by obj - useful to reset to template settings.
      this.defaultGroupByObj = deepClone(this.groupByObj);
    }

    if (daoConfig.extraColumnsDAOOptions !== undefined) {
      this.extrasColumnObj = daoConfig.extraColumnsDAOOptions;
      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('WITH_CAPITAL') !== -1) {
        this.displayCapital = true;
      }
      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('TARGET_ALLOCATIONS') !== -1) {
        this.displayTargetAllocations = true;
      }
      if (daoConfig.extraColumnsDAOOptions.columns.indexOf('TARGET_ALLOCATION_ADJUSTMENTS') !== -1) {
        this.displayTargetAllocationAdjustments = true;
      }
    }

    if (daoConfig.showRowHeaderColors !== undefined) {
      this.showRowHeaderColors = daoConfig.showRowHeaderColors;
    }

    if (daoConfig.labelOverride !== undefined) {
      this.labelOverride = daoConfig.labelOverride;
    }

    if (!this.showRowHeaderColors) {
      this.colorsDict = null;
    }

    if (this.proposal.riskProfile && this.displayTargetAllocations) {
      this.isRiskProfileTable = true;
    }

    if (this.groupByObj.order != null && !userHasPermissionEnabled(this.proposal.appUser, PERMISSIONS.SHOW_EQUITY_ASSET_ALLOCATION)) {
      this.groupByObj.order.forEach(orderObj => {
        if (orderObj.groupKey === ASSET_ALLOCATION_GROUPS.EQUITY) {
          const orderIndex = orderObj.keys.indexOf(PORTFOLIO_ASSET_ALLOCATION.EQ);
          orderObj.keys.splice(orderIndex, 1);
        }
      });
    }

    let itemDAO: ComparisonItemDAO | null = null;

    this.populateDetailedAssetWithPlaceholders();
    this.resetItemIndex();

    // Pre-scan to check if ANY item has "Other" allocation > 0
    let tempItemDAO: ComparisonItemDAO | null = null;
    while ((tempItemDAO = this.nextItem()) !== null) {
      const tempMissingAllocation = DataPoints.getItemMissingAllocationPortion(tempItemDAO.item);
      const otherAllocation = DataPoints.getAssetAllocationByKey(tempItemDAO.item, ASSET_ALLOCATION_GROUPS.OTHER);
      const otherValue = otherAllocation.response + tempMissingAllocation;
      if (otherValue > 0) {
        this.hasOtherCategoryWithValue = true;
        break; // Found at least one, no need to continue
      }
    }

    this.resetItemIndex();
    const validSubAllocations = new Set<string>();
    while ((itemDAO = this.nextItem()) !== null) {
      // We do not add the sub allocations names here if we are using tier one only allocations
      if (this.hasSubAllocations(itemDAO) && !this.isTierOneAllocationOnly()) {
        itemDAO.item?.allocations?.detailedAsset?.forEach(allocation => {
          allocation.subAllocations?.forEach(subAllocation => {
            const subAllocationName = subAllocation.name[LOCALE.EN_CA];
            if (this.subAllocationHasWeight(subAllocation.weight) && subAllocationName) {
              validSubAllocations.add(subAllocationName);
            }
          });
        });
      }
    }

    this.resetItemIndex();
    while ((itemDAO = this.nextItem()) !== null) {
      if (this.hasSubAllocations(itemDAO)) {
        const detailedAsset = itemDAO.item?.allocations?.detailedAsset || [];
        // Sort sub-allocations within each parent allocation for table view (grouped by parent)
        const sortedDetailedAsset = sortSubAllocations(detailedAsset);

        sortedDetailedAsset.forEach(allocation => {
          this.addCustomItemField(itemDAO as ComparisonItemDAO, allocation, undefined, validSubAllocations);
        });
      } else {
        this.groupByObj = deepClone(this.defaultGroupByObj);
        this.itemMissingAllocationPortion = DataPoints.getItemMissingAllocationPortion(itemDAO.item);
        let total: number = 0;
        let grandTotal: number = 0;

        // it should NEVER be undefined - assigned at construction, only reassigned if not undefined.
        if (this.groupByObj.order !== undefined) {
          const riskProfile = this.proposal.riskProfile || itemDAO.item.riskProfile;

          const splitCashAndFixedIncome = riskProfile && shouldSplitCashAndFixedIncome(riskProfile);
          if (!splitCashAndFixedIncome) {
            this.groupByObj.order = this.groupByObj.order.filter(order => order.groupKey !== ASSET_ALLOCATION_GROUPS.CASH);
            for (const order of this.groupByObj.order) {
              if (order.groupKey === ASSET_ALLOCATION_GROUPS.FIXED_INCOME) {
                order.labelKey = undefined;
                order.keys.push(PORTFOLIO_ASSET_ALLOCATION.CASH);
              }
            }
          }

          const isCanada = this.proposal.appUser.appConfig.appRegion === AppRegion.CAN;
          const dataProvider = getDataProviderForDisplay(this.proposal.appUser);

          for (const group of this.groupByObj.order) {
            if (group.groupKey !== null) {
              this.addGroup(itemDAO, group.groupKey, group.labelKey);
            }

            total = 0;
            if (
              (dataProvider === DATA_PROVIDER.FACTSET || dataProvider === DATA_PROVIDER.REFINITIV_US) &&
              group.groupKey === ASSET_ALLOCATION_GROUPS.EQUITY &&
              this.groupByObj.showElements
            ) {
              this.addItemField(itemDAO, PORTFOLIO_ASSET_ALLOCATION.EQ);
            } else {
              for (const key of group.keys) {
                // US users see Canadian Equity combined with International Equity
                if (isCanada || key !== PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ) {
                  const fieldValue = this.addItemField(itemDAO, key);

                  if (group.groupKey !== ASSET_ALLOCATION_GROUPS.EQUITY || key === PORTFOLIO_ASSET_ALLOCATION.EQ) {
                    total += fieldValue;
                  }
                }

                if (key === PORTFOLIO_ASSET_ALLOCATION.EQ && !this.groupByObj.showGrandTotal && this.groupByObj.showTotals) {
                  this.addItemField(itemDAO, key);
                }
              }
            }

            this.addTotal(itemDAO, group.groupKey || '', total, group.labelKey);
            grandTotal += total;
          }

          this.addGrandTotal(itemDAO, grandTotal);
          this.itemMissingAllocationPortion = 0;
        }
      }
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    const out: DonutData | null = super.getDonutData(index);

    if (!out) {
      return out;
    }

    // TODO - Make this more generic and apply to other DAOs
    const item = this.getItemDAOs()[index];
    if (this.hasSubAllocations(item) && !this.isTierOneAllocationOnly()) {
      // If sub-allocations exist, the donut data is already prepared by the build process.
      // We just need to filter out the parent allocations, so only sub-allocations are charted.
      const subAllocationNames = new Set<string>();
      item.item?.allocations?.detailedAsset?.forEach(asset => {
        asset.subAllocations?.forEach(sub => {
          subAllocationNames.add(formatLocalizedString(sub.name));
        });
      });

      if (out.data[0]) {
        // Filter to only show sub-allocations
        const subAllocationData = out.data[0].filter(d => subAllocationNames.has(d.name));

        // Sort all sub-allocations together for donut view (not grouped by parent)
        const sortedSubAllocations = sortSubAllocationsForDonut(subAllocationData);
        out.data[0] = sortedSubAllocations;
      }
    }

    // Apply paging logic similar to getTableData
    const pageOverflowIndex = this.proposalElement.pageOverflowIndex || 0;

    // Check if we have enough data to warrant paging
    if (out.data && out.data.length > 0 && out.data[0] && out.data[0].length > 0) {
      // Calculate maxRowsPerPage for donut chart using utility function
      const { orientation } = this.proposal.options;
      const maxRowsPerPage = calculateAssetAllocationDonutMaxItemsPerPage(orientation);

      const totalDataPoints = out.data[0].length;
      const totalPages = Math.ceil(totalDataPoints / maxRowsPerPage);

      // Only apply paging if we actually need multiple pages and the current page has data
      if (totalDataPoints > maxRowsPerPage && pageOverflowIndex < totalPages) {
        const startIndex = pageOverflowIndex * maxRowsPerPage;
        const endIndex = Math.min(startIndex + maxRowsPerPage, totalDataPoints);

        // Only show data if there are items for this page
        if (startIndex < totalDataPoints) {
          const pagedSeriesData = out.data.map(seriesArray => seriesArray.slice(startIndex, endIndex));
          out.data = pagedSeriesData;
        } else {
          // No data for this page - return empty
          out.data = out.data.map(() => []);
        }
      } else if (pageOverflowIndex === 0) {
        // First page: show content up to maxRowsPerPage or all content if it fits
        out.data = out.data.map(seriesArray => seriesArray.slice(0, Math.min(maxRowsPerPage, totalDataPoints)));
      } else {
        // Invalid page or no data needed for this page
        out.data = out.data.map(() => []);
      }
    }

    if (out.riskProfile && this.displayTargetAllocations) {
      const targets = DataPoints.getTargetAllocations(out.riskProfile);
      const outerSeries: SeriesData[] = [];
      for (const [key, value] of Object.entries(targets)) {
        outerSeries.push({
          name: key,
          y: (value.max + value.min) / 2,
        });
      }
      out.data.push(outerSeries);
    }

    if (this.colorsDict) {
      out.colorsDict = this.colorsDict;
    }

    return out;
  }

  /**
   * Get the number of additional pages needed for donut chart overflow.
   * Returns 0 if content fits within the default page fraction, or the number of additional pages needed.
   */
  getDonutPageCount(): number {
    const donutData = super.getDonutData(0);
    if (!donutData?.data?.[0]?.length) return 0;

    const { orientation } = this.proposal.options;
    const maxRowsPerPage = calculateAssetAllocationDonutMaxItemsPerPage(orientation);
    const totalPagesNeeded = Math.ceil(donutData.data[0].length / maxRowsPerPage);

    return Math.max(0, totalPagesNeeded - 1);
  }

  // private methods
  private addGroup(itemDAO: ComparisonItemDAO, key: string, labelKey?: string): void {
    if (this.groupByObj && this.groupByObj.showGroups) {
      const fieldName = this.formatMessage(`AssetAllocations.${labelKey || key}`);
      const field: ProposalFieldDAO = new ProposalFieldDAO(fieldName, null, ProposalStyleClassNames.ProposalTableGroup);
      field.cellType = TextCell;
      this.addFieldDAO(itemDAO, field);
      this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableGroup);
    }
  }

  private addTotal(itemDAO: ComparisonItemDAO, key: string, value: number, labelKey?: string): void {
    const label: string = this.groupByObj && !this.groupByObj.showGroups && key ? key : this.formatMessage('AssetAllocations.Other');
    const fieldName = key === '' ? this.formatMessage('AssetAllocations.Other') : this.formatMessage(`AssetAllocations.${labelKey || key}`);

    if (this.groupByObj && this.groupByObj.showTotals) {
      let color: string | undefined;
      if (this.colorsDict) {
        color = this.colorsDict[key];
      }

      if (this.itemMissingAllocationPortion > 0 && !this.displayTargetAllocations) {
        if (key.toUpperCase() === ASSET_ALLOCATION_GROUPS.OTHER) {
          value += this.itemMissingAllocationPortion;
        }
      }

      const field: ProposalFieldDAO = new ProposalFieldDAO(label, value ? value.toFixed(2) : 0, ProposalStyleClassNames.ProposalTableTotal);
      field.cellType = PercentageCell;
      if (this.isRiskProfileTable) {
        field.decimalPlace = 1;
      }

      this.addFieldDAO(itemDAO, field);
      this.addFieldName(fieldName, ProposalStyleClassNames.ProposalTableTotal, color);

      const riskProfile = this.proposal.riskProfile || itemDAO.item.riskProfile;

      // TODO - Make this generic
      if (riskProfile) {
        if (this.displayTargetAllocations) {
          const target = DataPoints.getTargetAllocationByKey(riskProfile, key);

          let targetAllocation: string;
          if (target) {
            if (target.min === target.max) {
              // If target is a single value, display only that value.
              targetAllocation = formatPercentage(target.max, 0, this.locale);
            } else {
              // Otherwise display the range, min-max
              targetAllocation = formatRiskProfileTargetMinMax(target.min, target.max, this.locale);
            }
          } else {
            targetAllocation = formatPercentage(0, 1, this.locale);
          }

          const extraField = new ProposalFieldDAO(label, targetAllocation, ProposalStyleClassNames.ProposalTableContent);
          extraField.cellType = TextCell;
          extraField.key = key;
          this.addFieldDAO(itemDAO, extraField, 1);
        }

        if (this.displayTargetAllocationAdjustments) {
          const adjustment: number = DataPoints.getAdjustmentForTargetAllocation(itemDAO.item, riskProfile, key);
          const classes = `${ProposalStyleClassNames.ProposalTableContent} ${ProposalStyleClassNames.ProposalTableAllocationAdjustments}`;
          const adjustmentField = new ProposalFieldDAO(label, adjustment > 0 ? { upside: Math.abs(adjustment) } : { downside: Math.abs(adjustment) }, classes);
          adjustmentField.cellType = UpDownCell;
          if (adjustment === 0) {
            adjustmentField.cellType = TextCell;
            adjustmentField.value = '-';
          }
          adjustmentField.key = key;
          this.addFieldDAO(itemDAO, adjustmentField, 2);
        }
      }
    }
  }

  private addGrandTotal(itemDAO: ComparisonItemDAO, value: number): void {
    if (this.groupByObj && this.groupByObj.showGrandTotal) {
      if (this.itemMissingAllocationPortion > 0) {
        value += this.itemMissingAllocationPortion;
      }
      const label: string = this.formatMessage('AssetAllocations.Total');
      const field: ProposalFieldDAO = new ProposalFieldDAO(label, value ? value.toFixed(2) : 0, ProposalStyleClassNames.ProposalTableTotal);
      field.cellType = PercentageCell;
      this.addFieldDAO(itemDAO, field);
      this.addFieldName(label, ProposalStyleClassNames.ProposalTableTotal);
      if (this.displayCapital) {
        const withCapital: number = 0.01 * parseFloat(value.toFixed(2)) * (itemDAO.item.size ? itemDAO.item.size : 1);
        const currency: string | undefined = itemDAO.item.currency === CURRENCY.MIXED ? CURRENCY.CAD : itemDAO.item.currency;
        const classes = `${ProposalStyleClassNames.ProposalTableContent} ${ProposalStyleClassNames.ProposalTableTotal}`;
        const extraField = new ProposalFieldDAO(label, { value: withCapital, currency }, classes);
        extraField.cellType = CurrencyCell;
        this.addFieldDAO(itemDAO, extraField, 1);
      }
    }
  }

  private addItemField(itemDAO: ComparisonItemDAO, key: string, labelKey?: string): number {
    const { locale } = this.proposal.options;
    const keyTarget = `AssetAllocations.${key}`;

    const _fieldName: string =
      (this.labelOverride && this.labelOverride[keyTarget] && this.labelOverride[keyTarget][locale]) ||
      this.formatMessage(`AssetAllocations.${labelKey || key}`);

    const assetAllocation = DataPoints.getAssetAllocationByKey(itemDAO.item, key);
    this.addDisclaimerKeys(assetAllocation.disclaimerKeys, itemDAO.item);
    const _allocationValue = assetAllocation.response;

    const { fieldName, allocationValue } = this.handleOther(key, _fieldName, _allocationValue, itemDAO);

    // If you don't want to show the elements just return the number
    if (this.groupByObj && !this.groupByObj.showElements) {
      return allocationValue;
    }

    this.addFieldDAOAndName(key, fieldName, allocationValue, itemDAO);

    return assetAllocation.response;
  }

  private addCustomItemField(
    itemDAO: ComparisonItemDAO,
    allocationDetails: LocalizedAllocationDetails,
    parentWeight?: number,
    validSubAllocations?: Set<string>,
  ) {
    const _fieldName = formatLocalizedString(allocationDetails.name);
    const disclaimerKeys = [PROPOSAL_DISCLAIMERS.ALLOCATIONS_AS_AT, PROPOSAL_DISCLAIMERS.INSTRUMENT_CUSTOM_DISCLAIMER];

    // For portfolios, we need to calculate the weighted allocation based on portfolio holdings
    let assetAllocation = allocationDetails.weight;

    if (itemDAO.item.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
      // For portfolios, calculate the weighted allocation from the underlying holdings
      assetAllocation = this.calculatePortfolioWeightedAllocation(itemDAO, allocationDetails, parentWeight);
    }

    const { [LOCALE.EN_CA]: key } = allocationDetails.name;
    if (!key) {
      return;
    }

    this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);
    const _allocationValue = assetAllocation;

    const { fieldName, allocationValue } = this.handleOther(key, _fieldName, _allocationValue, itemDAO);
    const isSubAllocation = parentWeight !== undefined;
    this.addFieldDAOAndName(key, fieldName, allocationValue, itemDAO, isSubAllocation);

    if (allocationDetails?.subAllocations?.length) {
      // Sort sub-allocations within this parent allocation for table view (maintains grouping)
      const sortedSubAllocations = sortSubAllocations(allocationDetails.subAllocations);
      sortedSubAllocations.forEach(subAllocation => {
        const subAllocationName = subAllocation.name[LOCALE.EN_CA];
        if (validSubAllocations && subAllocationName && validSubAllocations.has(subAllocationName)) {
          // Pass the current allocation weight as the parent weight for sub-allocations
          this.addCustomItemField(itemDAO, subAllocation, allocationValue, validSubAllocations);
        }
      });
    }
  }

  private addFieldDAOAndName(key: string, fieldName: string, allocationValue: number, itemDAO: ComparisonItemDAO, isSubAllocation: boolean = false): void {
    let color: string | undefined;
    if (this.colorsDict) {
      color = this.colorsDict[key];
    }

    const classes = [ProposalStyleClassNames.ProposalTableContent];
    if (!isSubAllocation) {
      classes.push(ProposalStyleClassNames.ProposalBold);
    }

    const field = new ProposalFieldDAO(fieldName, allocationValue ? allocationValue.toFixed(2) : 0, classes.join(' '));
    field.cellType = PercentageCell;
    field.key = key;

    this.addFieldDAO(itemDAO, field);

    // Add asterisk to "Other" fieldName if ANY item has Other > 0
    const isOther = key.toUpperCase() === ASSET_ALLOCATION_GROUPS.OTHER;
    const displayFieldName = isOther && this.hasOtherCategoryWithValue ? `${fieldName}*` : fieldName;

    const fieldClassNames = [...classes, displayFieldName.replaceAll(' ', '_')];
    if (isSubAllocation) {
      fieldClassNames.push(ProposalStyleClassNames.ProposalTableIndentField);
    }

    this.addFieldName(displayFieldName, fieldClassNames.join(' '), color);

    if (this.displayCapital) {
      const withCapital: number = 0.01 * allocationValue * (itemDAO.item.size ? itemDAO.item.size : 1);
      const currency: string | undefined = itemDAO.item.currency === CURRENCY.MIXED ? CURRENCY.CAD : itemDAO.item.currency;

      const extraField = new ProposalFieldDAO(fieldName, { value: withCapital, currency }, ProposalStyleClassNames.ProposalTableContent);
      extraField.cellType = CurrencyCell;
      extraField.key = key;

      this.addFieldDAO(itemDAO, extraField, 1);
    }
  }

  private handleOther(key: string, fieldName: string, allocationValue: number, itemDAO: ComparisonItemDAO): { fieldName: string; allocationValue: number } {
    const isOther = key.toUpperCase() === ASSET_ALLOCATION_GROUPS.OTHER;
    const hasMissingAllocation = this.itemMissingAllocationPortion > 0;

    if (hasMissingAllocation && isOther) {
      allocationValue += this.itemMissingAllocationPortion;
    }

    if (isOther && allocationValue > 0) {
      this.hasOtherCategoryWithValue = true;
      this.addDisclaimerKeys([PROPOSAL_DISCLAIMERS.ROLL_INTO_OTHER], itemDAO.item);
    }

    return {
      fieldName,
      allocationValue,
    };
  }

  /**
   * Calculate the weighted allocation for portfolios based on underlying holdings
   * @param itemDAO The comparison item (portfolio)
   * @param allocationDetails The allocation details to calculate weight for
   * @param parentWeight The parent allocation weight for sub-allocations
   * @returns The properly weighted allocation percentage
   */
  private calculatePortfolioWeightedAllocation(itemDAO: ComparisonItemDAO, allocationDetails: LocalizedAllocationDetails, parentWeight?: number): number {
    // If this is a sub-allocation and we have a parent weight, normalize it to sum to the parent allocation
    if (parentWeight !== undefined) {
      // Find the parent allocation to get all its sub-allocations for normalization
      const parentAllocation = this.findParentAllocation(itemDAO, allocationDetails);
      if (parentAllocation && parentAllocation.subAllocations && parentAllocation.subAllocations.length > 0) {
        // Calculate the total of all sub-allocation weights
        const totalSubAllocationWeight = parentAllocation.subAllocations.reduce((sum, subAlloc) => sum + subAlloc.weight, 0);

        if (totalSubAllocationWeight > 0) {
          // Normalize the sub-allocation to ensure all sub-allocations sum to the parent weight
          // Example: if parent is 25%, sub-allocation is 30%, and total sub-allocations are 50%,
          // then normalized result is 25% × (30% ÷ 50%) = 15%
          return (parentWeight * allocationDetails.weight) / totalSubAllocationWeight;
        }
      }

      return (parentWeight * allocationDetails.weight) / 100;
    }

    return allocationDetails.weight;
  }

  /**
   * Find the parent allocation that contains the given sub-allocation
   * @param itemDAO The comparison item
   * @param subAllocation The sub-allocation to find the parent for
   * @returns The parent allocation details or null if not found
   */
  private findParentAllocation(itemDAO: ComparisonItemDAO, subAllocation: LocalizedAllocationDetails): LocalizedAllocationDetails | null {
    if (!itemDAO.item?.allocations?.detailedAsset) {
      return null;
    }

    for (const allocation of itemDAO.item.allocations.detailedAsset) {
      if (allocation.subAllocations && allocation.subAllocations.length > 0) {
        const foundSubAllocation = allocation.subAllocations.find(sub => sub.name[LOCALE.EN_CA] === subAllocation.name[LOCALE.EN_CA]);
        if (foundSubAllocation) {
          return allocation;
        }
      }
    }

    return null;
  }

  /*
    If we have a comparison item with custom data and another comparison item without it,
    we need to populate it with placeholders so that the table can render correctly.
  */
  private populateDetailedAssetWithPlaceholders() {
    const processedObjects = [];
    let allAssets: LocalizedAllocationDetails[] = [];
    const objectsNeedingPopulation = [];

    // First pass: collect all unique assets
    let current = this.nextItem();
    while (current !== null) {
      if (current?.item?.allocations?.detailedAsset && current.item.allocations.detailedAsset.length > 0) {
        allAssets = this.mergeAssetArrays(allAssets, current.item.allocations.detailedAsset);
      } else if (current?.item?.allocations) {
        objectsNeedingPopulation.push(current.item.allocations);
      }
      processedObjects.push(current.item.allocations);
      current = this.nextItem();
    }

    // Second pass: apply the merged template to all objects while preserving original weights
    this.resetItemIndex();
    current = this.nextItem();
    while (current !== null) {
      if (current?.item?.allocations) {
        if (current.item.allocations.detailedAsset && current.item.allocations.detailedAsset.length > 0) {
          // For objects with existing assets, preserve their weights
          current.item.allocations.detailedAsset = this.applyTemplateWithOriginalWeights(allAssets, current.item.allocations.detailedAsset);
        } else {
          // For objects without assets, zero out weights
          current.item.allocations.detailedAsset = cloneWithZeroWeights(allAssets);
        }
      }
      current = this.nextItem();
    }

    // Helper function to deep clone and set weights to zero
    function cloneWithZeroWeights(assets: LocalizedAllocationDetails[]): LocalizedAllocationDetails[] {
      const clonedAssets = [];

      for (const asset of assets) {
        const clonedAsset: LocalizedAllocationDetails = {
          name: { ...asset.name },
          weight: 0,
        };
        clonedAssets.push(clonedAsset);
      }

      return clonedAssets;
    }

    return processedObjects;
  }

  private applyTemplateWithOriginalWeights(template: LocalizedAllocationDetails[], originalAssets: LocalizedAllocationDetails[]): LocalizedAllocationDetails[] {
    const result = [];

    for (const templateAsset of template) {
      const originalAsset = originalAssets.find(asset => asset.name[LOCALE.EN_CA] === templateAsset.name[LOCALE.EN_CA]);

      const resultAsset: LocalizedAllocationDetails = {
        name: { ...templateAsset.name },
        weight: originalAsset ? originalAsset.weight : 0,
      };

      if (templateAsset.subAllocations && templateAsset.subAllocations.length > 0) {
        resultAsset.subAllocations = templateAsset.subAllocations
          .map(templateSub => {
            const originalSub = originalAsset?.subAllocations?.find(sub => sub.name[LOCALE.EN_CA] === templateSub.name[LOCALE.EN_CA]);
            return {
              name: { ...templateSub.name },
              weight: originalSub?.weight || 0,
            };
          })
          .filter(Boolean) as LocalizedAllocationDetails[];
      }

      result.push(resultAsset);
    }

    return result;
  }

  private mergeAssetArrays(template: LocalizedAllocationDetails[], newAssets: LocalizedAllocationDetails[]): LocalizedAllocationDetails[] {
    const mergedAssets = [...template];

    // Add any new assets that don't exist in the template, or merge sub-allocations if they do exist
    for (const newAsset of newAssets) {
      const existingAssetIndex = mergedAssets.findIndex(asset => asset.name[LOCALE.EN_CA] === newAsset.name[LOCALE.EN_CA]);

      if (existingAssetIndex === -1) {
        mergedAssets.push({
          name: { ...newAsset.name },
          weight: newAsset.weight,
          subAllocations: newAsset.subAllocations
            ? newAsset.subAllocations.map(sub => ({
                name: { ...sub.name },
                weight: sub.weight,
              }))
            : undefined,
        });
      } else {
        const existingAsset = mergedAssets[existingAssetIndex];

        if (newAsset.subAllocations && newAsset.subAllocations.length > 0) {
          if (!existingAsset.subAllocations) {
            existingAsset.subAllocations = [];
          }

          // Merge sub-allocations - add any new ones that don't already exist
          for (const newSubAllocation of newAsset.subAllocations) {
            const existingSubAllocation = existingAsset.subAllocations.find(sub => sub.name[LOCALE.EN_CA] === newSubAllocation.name[LOCALE.EN_CA]);

            if (!existingSubAllocation) {
              existingAsset.subAllocations.push({
                name: { ...newSubAllocation.name },
                weight: newSubAllocation.weight,
              });
            }
          }
        }
      }
    }

    return mergedAssets;
  }

  /**
   * Override getTableData to support multi-page Asset allocation tables.
   * Uses pageOverflowIndex to return only the appropriate subset of rows for each page.
   */
  getTableData(skipRows?: number, limitRows?: number): ProposalTableData {
    const baseTableData = super.getTableData(skipRows, limitRows);
    const pageOverflowIndex = this.proposalElement.pageOverflowIndex || 0;

    // Calculate maxRowsPerPage using utility function to ensure consistency
    const { orientation } = this.proposal.options;
    const maxRowsPerPage = calculateAssetAllocationMaxRowsPerPage(orientation);

    // Calculate start and end indices for this page
    const startIndex = pageOverflowIndex * maxRowsPerPage;
    const endIndex = startIndex + maxRowsPerPage;

    return {
      ...baseTableData,
      rows: baseTableData.rows.slice(startIndex, endIndex),
    };
  }

  /**
   * Get the full row count without applying any paging logic.
   * This is used by ProposalBuilderComponent to calculate page fractions.
   */
  getFullRowCount(): number {
    return super.getTableData().rows.length;
  }

  private hasSubAllocations(item: ComparisonItemDAO): boolean {
    return (
      Boolean(
        item?.item?.allocations?.detailedAsset &&
        item.item.allocations.detailedAsset.length > 0 &&
        item.item.allocations.detailedAsset.some(allocation => allocation.subAllocations && allocation.subAllocations.length > 0),
      ) && !this.isInRiskProfileSection()
    );
  }

  private subAllocationHasWeight(weight: number | undefined): boolean {
    return Boolean(weight && weight != 0);
  }

  /**
   * Check if this DAO is being used within a specific section type
   * @param sectionKey The section key to search for
   * @returns true if we're in a section matching the provided key, false otherwise
   */
  private isInSection(sectionKey: string): boolean {
    let currentElement = this.proposalElement.parent;

    while (currentElement) {
      if (currentElement.elementType === ProposalElementNS.ProposalElementType.Section) {
        if (currentElement.sectionKey && currentElement.sectionKey.includes(sectionKey)) {
          return true;
        }
      }
      currentElement = currentElement.parent;
    }

    return false;
  }

  /**
   * Check if this DAO is being used within a RISK_PROFILE section
   * @returns true if we're in a RISK_PROFILE section, false otherwise
   */
  private isInRiskProfileSection(): boolean {
    return this.isInSection(PROPOSAL_SECTION_KEYS.RISK_PROFILE);
  }

  /**
   * Check if this DAO is being used within an ASSET_ALLOCATION_TIER_ONE_ONLY section
   * @returns true if we're in an ASSET_ALLOCATION_TIER_ONE_ONLY section, false otherwise
   */
  private isTierOneAllocationOnly(): boolean {
    return this.isInSection(PROPOSAL_SECTION_KEYS.ASSET_ALLOCATION_TIER_ONE_ONLY);
  }
}
